import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, ChevronDown, ArrowUpCircle } from 'lucide-react';
import RedeemModal from '../../components/rewards/RedeemModal';
import ConfirmRedeemModal from '../../components/rewards/ConfirmRedeemModal';
import SuccessModal from '../../components/rewards/SuccessModal';

const Reward = () => {
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState('2023');
  const [redeemData, setRedeemData] = useState(null);

  // Mock user points data - replace with actual API call
  const pointsData = {
    pointsThisMonth: 14215,
    totalPointsAllTime: 186,
    availableToRedeem: 123132,
    monthlyIncrease: 3, // percentage
  };

  const howItWorksSteps = [
    {
      number: '1',
      title: 'Purchase more Tripsecure insurance policy',
      description: 'Every policy purchase earns you points'
    },
    {
      number: '2', 
      title: 'You will get point for your purchase anytime',
      description: 'Points are automatically added to your account'
    },
    {
      number: '3',
      title: 'You can redeem this point for airtime and data bundle',
      description: 'Convert points to airtime or data bundles easily'
    }
  ];

  const redeemHistory = [
    { id: 1, date: '2023-12-15', points: 5000, type: 'Data Bundle', amount: '5GB', status: 'completed' },
    { id: 2, date: '2023-12-10', points: 2000, type: 'Airtime', amount: 'GHS 20', status: 'completed' },
    { id: 3, date: '2023-12-05', points: 3000, type: 'Data Bundle', amount: '3GB', status: 'completed' },
  ];

  const handleRedeemClick = () => {
    setShowRedeemModal(true);
  };

  const handleRedeemSubmit = (data) => {
    setRedeemData(data);
    setShowRedeemModal(false);
    setShowConfirmModal(true);
  };

  const handleConfirmRedeem = () => {
    setShowConfirmModal(false);
    // Simulate API call
    setTimeout(() => {
      setShowSuccessModal(true);
    }, 1000);
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    setRedeemData(null);
  };

  return (
    <div className="px-4 pt-20 pb-4 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 sm:pt-24 sm:pb-6 sm:px-6 lg:pt-28 lg:pb-8 lg:px-8">
      <div className="mx-auto max-w-8xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="mb-2 text-3xl font-bold text-gray-900">TripSecure Points Dashboard</h1>
          <p className="text-gray-600">Manage your rewards and redeem points for great benefits</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-3">
          {/* Refer A Friend Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="overflow-hidden relative p-6 bg-gradient-to-br from-emerald-100 to-green-200 rounded-2xl lg:col-span-1"
          >
            <div className="relative z-10 pr-20">
              <h3 className="mb-2 text-lg font-semibold text-emerald-800">Refer A Friend!</h3>
              <p className="mb-4 text-sm text-emerald-700">
                Refer your friends to tripsecure, you will earn a share of their booking fees
              </p>
            </div>
            <div className="absolute right-6 top-1/2 transform -translate-y-1/2">
              <Gift className="w-14 h-14 text-orange-500" />
            </div>
          </motion.div>

          {/* Points Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 bg-white rounded-2xl shadow-lg lg:col-span-2"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {/* Points This Month */}
              <div className="text-center">
                <p className="mb-2 text-sm text-gray-600">Points this month</p>
                <div className="flex justify-center items-center">
                  <span className="text-3xl font-bold text-gray-900">{pointsData.pointsThisMonth.toLocaleString()}</span>
                </div>
                <div className="flex justify-center items-center mt-2">
                  <ArrowUpCircle className="mr-1 w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-medium text-emerald-600">+{pointsData.monthlyIncrease}%</span>
                  <span className="ml-1 text-xs text-gray-500">vs previous month</span>
                </div>
              </div>

              {/* Total Points */}
              <div className="text-center">
                <p className="mb-2 text-sm text-gray-600">Total Points (All-time)</p>
                <div className="flex justify-center items-center">
                  <span className="text-3xl font-bold text-gray-900">{pointsData.totalPointsAllTime.toLocaleString()}</span>
                </div>
              </div>

              {/* Available to Redeem */}
              <div className="text-center">
                <p className="mb-2 text-sm text-gray-600">Available to Redeem</p>
                <div className="flex justify-center items-center mb-4">
                  <span className="text-3xl font-bold text-emerald-600">{pointsData.availableToRedeem.toLocaleString()}</span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleRedeemClick}
                  className="px-6 py-2 font-medium text-white bg-emerald-500 rounded-lg transition-colors hover:bg-emerald-600"
                >
                  Redeem
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* How It Works */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="p-6 bg-white rounded-2xl shadow-lg"
          >
            <h3 className="mb-6 text-xl font-semibold text-gray-900">How It Works</h3>
            <div className="space-y-4">
              {howItWorksSteps.map((step, index) => (
                <div key={step.number} className="flex items-start">
                  <div className="flex flex-shrink-0 justify-center items-center mr-4 w-8 h-8 text-sm font-semibold text-white bg-emerald-500 rounded-full">
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <h4 className="mb-1 font-medium text-gray-900">{step.title}</h4>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-right">
              <button className="text-sm font-medium text-emerald-600 hover:text-emerald-700">
                Need Help?
              </button>
            </div>
          </motion.div>

          {/* Redeem History */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="p-6 bg-white rounded-2xl shadow-lg"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Redeem History</h3>
              <div className="relative">
                <select 
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="px-4 py-2 pr-8 text-sm bg-gray-50 rounded-lg border border-gray-200 appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="2023">2023</option>
                  <option value="2022">2022</option>
                  <option value="2021">2021</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <div className="flex mb-4 space-x-2">
              <button className="px-3 py-1 text-xs font-medium text-white bg-emerald-500 rounded-full">
                All
              </button>
              <button className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200">
                Reward points
              </button>
              <button className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200">
                Redeem points
              </button>
            </div>

            <div className="space-y-3">
              {redeemHistory.map((item) => (
                <div key={item.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="mr-3 w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.type}</p>
                      <p className="text-xs text-gray-500">{item.date}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{item.amount}</p>
                    <p className="text-xs text-gray-500">-{item.points.toLocaleString()} pts</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Modals */}
      <RedeemModal
        open={showRedeemModal}
        onClose={() => setShowRedeemModal(false)}
        onSubmit={handleRedeemSubmit}
        maxPoints={pointsData.availableToRedeem}
      />

      <ConfirmRedeemModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmRedeem}
        redeemData={redeemData}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={handleSuccessClose}
        redeemData={redeemData}
      />
    </div>
  );
};

export default Reward;